import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ClipboardList, ArrowRight, StickyNote } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Reveal } from "@/components/common/Reveal";
import EmptyState from "@/components/common/EmptyState";
import { useInquiryCart } from "@/context/InquiryCartContext";
import { brandName, categoryName } from "@/lib/labels";
import { useSite } from "@/context/SiteDataContext";

export default function InquiryCart() {
  const SITE = useSite();
  const { items, count, setQuantity, setNotes, removeItem, clear } = useInquiryCart();

  return (
    <>
      <Seo routeKey="cart" title="Inquiry Cart" description="Review the products you've selected and proceed to send an enquiry to Athena Scientific." canonical={`https://${SITE.website}/cart`} />
      <div className="container-x pt-8">
        <Breadcrumbs crumbs={[{ label: "Inquiry Cart" }]} />
      </div>

      <div className="container-x py-10 md:py-14">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-ink">Inquiry Cart</h1>
          <p className="mt-3 text-greyish">Review your selected products, add notes, then proceed to send an enquiry. This is not an online purchase.</p>
        </Reveal>

        {items.length === 0 ? (
          <div className="mt-8 border border-border rounded-lg">
            <EmptyState icon={ClipboardList} title="Your inquiry cart is empty" description="Browse our catalogue and add the products you'd like to enquire about." actionLabel="Explore Products" actionTo="/products" testId="cart-page-empty" />
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-4">
              {items.map((it) => (
                <div key={it.productId} data-testid={`cartpage-item-${it.slug}`} className="flex flex-col sm:flex-row gap-5 p-5 border border-border rounded-lg bg-white">
                  <Link to={`/products/${it.slug}`} className="shrink-0"><img src={it.image} alt={it.name} className="w-full sm:w-28 h-40 sm:h-28 rounded-md object-cover bg-[#f6f8f9]" /></Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[0.65rem] uppercase tracking-widest font-semibold text-greyish">{brandName(it.brand)} · {categoryName(it.category)}</p>
                        <Link to={`/products/${it.slug}`} className="block text-lg font-bold text-ink hover:text-aqua transition-colors">{it.name}</Link>
                      </div>
                      <button onClick={() => removeItem(it.productId)} data-testid={`cartpage-remove-${it.slug}`} aria-label="Remove" className="text-greyish hover:text-destructive transition-colors"><Trash2 className="w-5 h-5" /></button>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-4">
                      <div className="inline-flex items-center border border-border rounded-md">
                        <button onClick={() => setQuantity(it.productId, it.quantity - 1)} data-testid={`cartpage-dec-${it.slug}`} className="w-9 h-9 flex items-center justify-center text-greyish hover:text-ink"><Minus className="w-4 h-4" /></button>
                        <span className="w-10 text-center font-semibold">{it.quantity}</span>
                        <button onClick={() => setQuantity(it.productId, it.quantity + 1)} data-testid={`cartpage-inc-${it.slug}`} className="w-9 h-9 flex items-center justify-center text-greyish hover:text-ink"><Plus className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="mt-4 relative">
                      <StickyNote className="absolute left-3 top-3 w-4 h-4 text-greyish" />
                      <textarea value={it.notes} onChange={(e) => setNotes(it.productId, e.target.value)} data-testid={`cartpage-notes-${it.slug}`} rows={2} placeholder="Add a note for this product (optional)…" className="w-full pl-9 pr-3 py-2.5 text-sm border border-border rounded-md outline-none focus:border-aqua focus:ring-2 focus:ring-aqua/20 transition-all resize-none" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2">
                <Link to="/products" className="text-sm font-semibold text-ink hover:text-aqua transition-colors">← Continue browsing</Link>
                <button onClick={clear} data-testid="cartpage-clear" className="text-sm text-greyish hover:text-destructive transition-colors">Clear cart</button>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="sticky top-28 rounded-lg border border-border bg-[#f9fafb] p-7">
                <p className="eyebrow">Summary</p>
                <div className="mt-4 flex items-center justify-between text-ink"><span className="text-greyish">Products selected</span><span className="text-2xl font-bold">{count}</span></div>
                <p className="mt-4 text-sm text-greyish">Send us your selection and our team will follow up with details, documentation and a tailored recommendation.</p>
                <Link to="/enquiry" data-testid="cartpage-proceed" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-aqua text-white font-semibold px-6 py-3.5 rounded-md hover:bg-aqua-dark transition-colors">Proceed to Enquiry <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
